import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Button, Group, Paper, Stack, Text, Title, Tooltip } from '@mantine/core'
import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useState } from 'react'

import useCheckMobileScreen from '@/hooks/useCheckMobileScreen'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { FactField, FactFieldRecord, FieldType, SponsorFactFieldRecord } from '@/services/api/api.types'

import { ConfirmChangesModal } from './ConfirmChangesModal'
import { SortableHeader } from './SortableHeader'
import type { HeaderField } from './types'

// Load and parse API fact field data
function loadFactFieldsFromApi(
  apiSponsorFactFields: SponsorFactFieldRecord[],
  apiFactFields: FactFieldRecord[],
): {
  factFields: FactField[]
  matchedPairs: Array<{ sponsorRecord: SponsorFactFieldRecord; factRecord: FactFieldRecord | undefined }>
  unmatchedFacts: FactFieldRecord[]
  fieldsTree: Array<HeaderField>
} {
  console.log('[loadFactFieldsFromApi] loading fields')

  const factsMapById = new Map(apiFactFields.map(f => [f.id, f]))
  const factsMapByName = new Map(apiFactFields.map(f => [f.name, f]))

  // 1. Match all facts and sponsor facts into pairs
  const currentMatchedPairs: Array<{
    sponsorRecord: SponsorFactFieldRecord
    factRecord: FactFieldRecord | undefined
  }> = []
  const linkedFactIds = new Set<string>()

  for (const sponsorFact of apiSponsorFactFields) {
    let correspondingFact: FactFieldRecord | undefined = undefined

    // FIXME: why does factId sometimes reference an actual id, and sometimes a name?
    if (sponsorFact.factId && factsMapById.has(sponsorFact.factId)) {
      correspondingFact = factsMapById.get(sponsorFact.factId)
    } else if (sponsorFact.factId && factsMapByName.has(sponsorFact.factId)) {
      correspondingFact = factsMapByName.get(sponsorFact.factId)
    } else if (
      !sponsorFact.factId &&
      (sponsorFact.factType === 'core' || sponsorFact.factType === 'sponsor') &&
      factsMapByName.has(sponsorFact.name)
    ) {
      correspondingFact = factsMapByName.get(sponsorFact.name)
    }

    if (correspondingFact) {
      linkedFactIds.add(correspondingFact.id)
    }
    currentMatchedPairs.push({ sponsorRecord: sponsorFact, factRecord: correspondingFact })
  }
  console.log(`[loadFactFieldsFromApi] found ${currentMatchedPairs.length} matched pairs`)

  // 2. Make a list of any facts lacking a record in sponsor facts
  const currentUnmatchedFacts = apiFactFields.filter(fact => !linkedFactIds.has(fact.id))
  console.log(`[loadFactFieldsFromApi] found ${currentUnmatchedFacts.length} unmatched facts`)

  // 3. Construct a list of FactField for every sponsor fact (that is a fact, not group/header)
  const fieldTypeMapping: { [key: string]: FieldType } = {
    string: 'string',
    enum: 'enum',
    date: 'date',
  }

  const parsedSponsorFactFields: FactField[] = []
  for (const pair of currentMatchedPairs) {
    const { sponsorRecord, factRecord } = pair

    // These are the fields that differ between an actual input field and a header/group
    // FIXME: FactFields and Headers are both very similar in representation in the API, although
    // a Header is just used for grouping and is not itself a fact.
    let fieldType: FieldType | null = null
    let options: string[] | undefined = undefined

    if (sponsorRecord.factType === 'core' || sponsorRecord.factType === 'sponsor') {
      const apiFieldType = sponsorRecord.fieldType || factRecord?.fieldType
      const uiFieldType = apiFieldType ? fieldTypeMapping[apiFieldType] || 'string' : 'string'

      fieldType = uiFieldType
      options = uiFieldType === 'enum' ? factRecord?.enums || undefined : undefined
    } else if (sponsorRecord.factType === 'header') {
      fieldType = null
      options = undefined
    } else {
      console.warn('[loadFactFieldsFromApi] unhandled fact type', { sponsorRecord })
    }

    // FIXME: Support old sponsor fact data that uses the negative ordering hack.
    // Remove this after all the old data is confirmed cleaned up.
    //   Negative order should be treated as absolute value order
    //   Negative order is disabled state
    let factOrder: number = sponsorRecord.factOrder
    let enabled: boolean = sponsorRecord.active
    if (factOrder < 0) {
      console.warn('[loadFactFieldsFromApi] handling legacy negative fact_order', { sponsorRecord })
      factOrder = Math.abs(sponsorRecord.factOrder)
      enabled = false
    }

    const factField: FactField = {
      id: sponsorRecord.id, // Using sponsorRecord.id as the primary ID for these UI elements
      name: sponsorRecord.name,
      fieldType: fieldType,
      factType: sponsorRecord.factType,
      headerId: sponsorRecord.headerId,
      factOrder: factOrder,
      enabled: enabled,
      options: options,
    }

    parsedSponsorFactFields.push(factField)
  }

  // Remove duplicate fields
  const duplicatedFields = parsedSponsorFactFields.filter(
    (field, index, self) => index === self.findIndex(t => t.id !== field.id),
  )
  const uniqueFields = parsedSponsorFactFields.filter(
    (field, index, self) => index === self.findIndex(t => t.id === field.id),
  )

  if (duplicatedFields.length > 0) {
    console.warn('[loadFactFieldsFromApi] duplicate fields found', { parsedSponsorFactFields, duplicatedFields })
  }

  // Sort fields by their factOrder for display order
  const sortedFields = uniqueFields.sort((a, b) => a.factOrder - b.factOrder)

  // 4. Build tree structure of headers and their child facts
  const headers = sortedFields.filter(field => field.factType === 'header')
  const facts = sortedFields.filter(field => field.factType === 'core' || field.factType === 'sponsor')

  // Create a map of headers by their header ID for quick lookup
  const headersMap = new Map(headers.map(header => [header.headerId, header]))

  // Find facts without parent headers
  const factsWithoutHeaders = facts.filter(fact => !fact.headerId || !headersMap.has(fact.headerId))
  if (factsWithoutHeaders.length > 0) {
    console.warn('[loadFactFieldsFromApi] Found fact fields with a missing header, or with no header', {
      factsWithoutHeaders,
    })
  }

  // Build the tree structure
  const fieldsTree: Array<HeaderField> = []

  // Add headers with their children
  for (const header of headers) {
    const children = facts.filter(fact => fact.headerId === header.headerId)

    if (children.length === 0) {
      console.warn('[loadFactFieldsFromApi] Found header without child fact fields', { header })
    }

    const headerWithChildren: HeaderField = {
      ...header,
      children: children,
    }
    fieldsTree.push(headerWithChildren)
  }

  return {
    factFields: sortedFields,
    matchedPairs: currentMatchedPairs,
    unmatchedFacts: currentUnmatchedFacts,
    fieldsTree,
  }
}

interface FactFieldsPanelProps {
  showTutorialText: boolean
  registerUnsavedChangesChecker: (tabKey: 'factFields', checker: () => boolean, onDiscard: () => void) => void
}

export function FactFieldsPanel({ showTutorialText = false, registerUnsavedChangesChecker }: FactFieldsPanelProps) {
  const { api } = useGlobalState()
  const isMobile = useCheckMobileScreen()
  const [isPending, setIsPending] = useState(false)

  // Get relevant fact field data from API
  const { data: apiSponsorFactFields, refetch: refetchSponsorFactFields } = useQuery({
    queryKey: ['sponsorFactFields'],
    queryFn: () => api.getSponsorFactFields(),
  })
  const { data: apiFactFields } = useQuery({
    queryKey: ['factFields'],
    queryFn: () => api.getAllFactFields(),
  })

  // States to hold processed data based on API responses
  const [_matchedPairs, setMatchedPairs] = useState<
    Array<{ sponsorRecord: SponsorFactFieldRecord; factRecord: FactFieldRecord | undefined }>
  >([])
  const [_unmatchedFacts, setUnmatchedFacts] = useState<FactFieldRecord[]>([])

  // The tree is the primary UI data structure for managing header and field hierarchy and sorting
  const [factFieldsTree, setFactFieldsTree] = useState<HeaderField[]>([])
  const [lastSavedFactFieldsTree, setLastSavedFactFieldsTree] = useState<HeaderField[] | null>(null)

  const [activeHeaderId, setActiveHeaderId] = useState<string | null>(null)
  const [activeHeader, setActiveHeader] = useState<HeaderField | null>(null)
  const [showConfirmModal, setShowConfirmModal] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const hasChanges = useMemo(() => {
    return lastSavedFactFieldsTree !== null
  }, [lastSavedFactFieldsTree])

  const deepCopyFactFieldsTree = (tree: HeaderField[]): HeaderField[] => {
    return tree.map(header => ({
      ...header,
      children: header.children ? header.children.map(child => ({ ...child })) : undefined,
    }))
  }

  // Helper function to save a snapshot of the current state
  // We only want to save a snapshot if we don't already have one, as we don't
  // want to overwrite an existing snapshot with new state.
  const saveSnapshotIfNeeded = () => {
    if (lastSavedFactFieldsTree === null) {
      setLastSavedFactFieldsTree(deepCopyFactFieldsTree(factFieldsTree))
    }
  }

  const revertFactFields = () => {
    if (!lastSavedFactFieldsTree) {
      console.warn('[revertFactFields] no last saved fact fields tree')
      return
    }

    setFactFieldsTree(lastSavedFactFieldsTree)
    setLastSavedFactFieldsTree(null)
  }

  // Load and parse fact fields whenever the API data changes
  useEffect(() => {
    if (apiSponsorFactFields && apiFactFields) {
      const { matchedPairs, unmatchedFacts, fieldsTree } = loadFactFieldsFromApi(apiSponsorFactFields, apiFactFields)
      setMatchedPairs(matchedPairs)
      setUnmatchedFacts(unmatchedFacts)
      setFactFieldsTree(fieldsTree)
    }
  }, [apiSponsorFactFields, apiFactFields])

  const headerIds: string[] = factFieldsTree.map(header => header.id)

  // Helper function to build flat factFields array from the header and fields tree.
  const flattenFactFieldsTree = (tree: HeaderField[]): FactField[] => {
    return tree.flatMap(header => {
      return [header, ...(header.children || [])]
    })
  }

  // Use the sort order in the flattened list as the fact order
  const computeFactOrder = (factFields: FactField[]) => {
    return factFields.map((field, index) => {
      return { ...field, factOrder: index }
    })
  }

  const handleConfirmApply = async () => {
    if (!factFieldsTree) {
      console.warn('[handleConfirmApply] no fact fields tree')
      return
    }

    setIsPending(true)
    try {
      // Flatten the tree and compute the correct fact orders to reflect the UI sort order
      const updatedFactOrder = computeFactOrder(flattenFactFieldsTree(factFieldsTree))

      await api.updateSponsorFactFields(updatedFactOrder, (await api.checkUser()).sponsorId)

      setLastSavedFactFieldsTree(null)
      refetchSponsorFactFields()
    } catch (error) {
      console.error('Error updating fact fields:', error)
    } finally {
      setIsPending(false)
      setShowConfirmModal(false)
    }
  }

  const handleHeaderDragStart = (event: DragStartEvent) => {
    const { active } = event
    const id = active.id.toString()

    const header = factFieldsTree.find(header => header.id === id)
    if (header) {
      setActiveHeader(header)
      setActiveHeaderId(id)
    }
  }

  // Handle reordering of headers at the top level
  const handleHeaderDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    // Reset header drag state
    setActiveHeaderId(null)
    setActiveHeader(null)

    if (!over || active.id === over.id) return

    const activeId = active.id as string
    const overId = over.id as string

    setFactFieldsTree(tree => {
      const oldIndex = tree.findIndex(header => header.id === activeId)
      const newIndex = tree.findIndex(header => header.id === overId)

      if (oldIndex === -1 || newIndex === -1) return tree

      // Save a snapshot before making changes
      saveSnapshotIfNeeded()

      const reorderedTree = arrayMove(tree, oldIndex, newIndex)

      return reorderedTree
    })
  }

  // Handle reordering of fields within any header
  const handleFieldReorder = (headerId: string, activeId: string, overId: string) => {
    setFactFieldsTree(tree => {
      const headerIndex = tree.findIndex(header => header.id === headerId)
      if (headerIndex === -1) return tree

      const header = tree[headerIndex]
      if (!header.children) return tree

      const oldIndex = header.children.findIndex(field => field.id === activeId)
      const newIndex = header.children.findIndex(field => field.id === overId)

      if (oldIndex === -1 || newIndex === -1) return tree

      // Save a snapshot before making changes
      saveSnapshotIfNeeded()

      // Create updated tree with reordered children
      const newTree = [...tree]
      newTree[headerIndex] = {
        ...header,
        children: arrayMove(header.children, oldIndex, newIndex),
      }

      return newTree
    })
  }

  // When fields change we need to maintain the header and children enabled/disabled relationship
  //   A newly disabled header disables all children
  //   A newly enabled header enables all children
  //   Children can be independently enabled
  //   A disabled header becomes enabled when a child in enabled, but does not enable other children
  const handleFieldChange = (field: FactField) => {
    // Save a snapshot before making changes
    saveSnapshotIfNeeded()

    const newTree = factFieldsTree.map(header => {
      // Changed field is this header
      if (header.id === field.id) {
        // Update header itself
        const updatedHeader: HeaderField = { ...header, ...field }

        // If header is being enabled, enable all its children
        if (field.enabled && header.children) {
          updatedHeader.children = header.children.map(child => ({
            ...child,
            enabled: true,
          }))
        } else if (!field.enabled && header.children) {
          // If header is being disabled, disable all its children
          updatedHeader.children = header.children.map(child => ({
            ...child,
            enabled: false,
          }))
        }

        return updatedHeader
      }

      // Field might be in the children of current header
      const foundChild = header.children?.find(child => child.id === field.id)
      if (header.children && foundChild) {
        // Update the child matching this field
        const updatedChildren = header.children.map(child => (child.id === field.id ? field : child))
        // Check if the header also needs to be enabled (it must be enabled if at least one child is)
        const headerEnabled = updatedChildren.some(child => child.enabled)
        return { ...header, enabled: headerEnabled, children: updatedChildren }
      }

      return header
    })

    setFactFieldsTree(newTree)
  }

  // Handle cancel and save
  const handleCancel = useCallback(() => {
    console.log('[handleCancel]')
    if (!lastSavedFactFieldsTree) {
      console.warn('[handleCancel] no last saved fact fields tree')
      return
    }

    revertFactFields()
  }, [lastSavedFactFieldsTree, revertFactFields])

  // Register unsaved changes checker with parent
  useEffect(() => {
    registerUnsavedChangesChecker('factFields', () => hasChanges, handleCancel)
  }, [registerUnsavedChangesChecker, hasChanges, handleCancel])

  const handleSave = () => {
    console.log('[handleSave]')
    setShowConfirmModal(true)
  }

  return (
    <Stack>
      <Paper radius="md" p={isMobile ? 'md' : 'lg'} shadow="sm" px={isMobile ? 'md' : 'xl'}>
        <Group justify="space-between" align="center" mb="md">
          <Stack gap="xs">
            <Title order={isMobile ? 6 : 3} mb={isMobile ? 'md' : undefined}>
              Design the End User Profile
            </Title>
          </Stack>
          <Group>
            <Group>
              <Button variant="outline" onClick={handleCancel} disabled={!hasChanges}>
                Cancel
              </Button>
              <Tooltip label="Save becomes available when you make changes. Save or Cancel any changes.">
                <Button onClick={handleSave} disabled={!hasChanges}>
                  Save
                </Button>
              </Tooltip>
            </Group>
          </Group>
        </Group>

        {showTutorialText && (
          <Text size="md" mb="md" c="dimmed">
            Select which fields appear in the end user profile.
          </Text>
        )}

        <DndContext
          sensors={sensors}
          onDragStart={handleHeaderDragStart}
          onDragEnd={handleHeaderDragEnd}
          collisionDetection={closestCenter}>
          <SortableContext items={headerIds} strategy={verticalListSortingStrategy}>
            <Stack gap="lg">
              {factFieldsTree.map(header => (
                <SortableHeader
                  key={header.id}
                  header={header}
                  onFieldChange={handleFieldChange}
                  onFieldReorder={handleFieldReorder}
                />
              ))}
            </Stack>
          </SortableContext>
          <DragOverlay>{activeHeaderId && activeHeader ? <SortableHeader header={activeHeader} /> : null}</DragOverlay>
        </DndContext>
      </Paper>

      <ConfirmChangesModal
        title="Are you sure?"
        description="Making changes to user profile fields can impact the experience of End Users who have already set up their profiles."
        opened={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmApply}
        isLoading={isPending}
      />

      <style>{`
        /* Ensure DnD context creates a new stacking context */
        [data-dnd-kit-context] {
          position: relative;
          z-index: 0;
        }
      `}</style>
    </Stack>
  )
}
